import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Package, ShoppingCart, FileText, TrendingUp } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DashboardHome = () => {
  const { vendor } = useAuth();
  const [stats, setStats] = useState({
    products: 0,
    orders: 0,
    pages: 0,
    revenue: 0,
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    const token = localStorage.getItem('vendor_token');
    try {
      const [productsRes, ordersRes, pagesRes] = await Promise.all([
        axios.get(`${API}/products`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/orders`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/pages`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);

      const orders = ordersRes.data.orders || [];
      const revenue = orders
        .filter((o) => o.status === 'paid')
        .reduce((sum, o) => sum + o.total_amount, 0);

      setStats({
        products: productsRes.data.products?.length || 0,
        orders: orders.length,
        pages: pagesRes.data.pages?.length || 0,
        revenue,
      });
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const statCards = [
    {
      title: 'Total Products',
      value: stats.products,
      icon: Package,
      color: 'text-blue-600',
    },
    {
      title: 'Total Orders',
      value: stats.orders,
      icon: ShoppingCart,
      color: 'text-green-600',
    },
    {
      title: 'Custom Pages',
      value: stats.pages,
      icon: FileText,
      color: 'text-purple-600',
    },
    {
      title: 'Revenue',
      value: `₹${stats.revenue.toFixed(2)}`,
      icon: TrendingUp,
      color: 'text-orange-600',
    },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div data-testid="dashboard-home">
      <div className="mb-8">
        <h2 className="text-3xl font-bold tracking-tight">Welcome, {vendor?.business_name}!</h2>
        <p className="text-muted-foreground mt-2">Here's an overview of your store</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {statCards.map((stat) => {
          const Icon = stat.icon;
          return (
            <Card key={stat.title}>
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">
                  {stat.title}
                </CardTitle>
                <Icon className={`h-4 w-4 ${stat.color}`} />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{stat.value}</div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      <Card className="mt-8">
        <CardHeader>
          <CardTitle>Quick Links</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <a href="/dashboard/products" className="p-4 border rounded-md hover:bg-accent transition-colors">
              <Package className="h-5 w-5 mb-2" />
              <h3 className="font-medium">Manage Products</h3>
              <p className="text-sm text-muted-foreground mt-1">Add or edit your products</p>
            </a>
            <a href="/dashboard/orders" className="p-4 border rounded-md hover:bg-accent transition-colors">
              <ShoppingCart className="h-5 w-5 mb-2" />
              <h3 className="font-medium">View Orders</h3>
              <p className="text-sm text-muted-foreground mt-1">Check recent orders</p>
            </a>
            <a href="/dashboard/manage?tab=qr-code" className="p-4 border rounded-md hover:bg-accent transition-colors">
              <FileText className="h-5 w-5 mb-2" />
              <h3 className="font-medium">Your QR Code</h3>
              <p className="text-sm text-muted-foreground mt-1">Download and share</p>
            </a>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default DashboardHome;